import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { SpinnerGap, ArrowLeft, CurrencyEur } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const MONTHS = ['','Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
const COLORS = ['#0F1829','#4F6DC7','#00C853','#FFD600','#FF6B2B','#FF2B2B','#9333EA','#6B7280'];

export default function CommissionDetailPage() {
  const { ano, mes } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);

  useEffect(() => {
    api.post('/comissoes/fullDetail', { Ano: parseInt(ano), Mes: parseInt(mes) })
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [ano, mes]);

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0F1829]" /></div>;
  if (!data) return null;

  const resumo = data.Resumo || {};
  const detalhes = data.Detalhes || [];
  const porTipo = data.PorTipo || [];

  return (
    <div data-testid="commission-detail-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <button onClick={() => navigate('/comissoes')} className="flex items-center gap-1 text-sm text-[#0F1829] hover:underline mb-4"><ArrowLeft size={14} /> Voltar</button>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Comissão {MONTHS[parseInt(mes)]} {ano}</h1>
        <p className="text-sm text-[#6B7280] mt-1">Detalhe completo de comissões do mês</p>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280]">Valor Total</p>
          <p className="text-xl font-bold font-mono text-[#0F1829] mt-1">{fmt(resumo.ValorTotal)}</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280]">Comissão</p>
          <p className="text-xl font-bold font-mono text-[#00C853] mt-1">{fmt(resumo.ValorComissao)}</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280]">NetPoints</p>
          <p className="text-xl font-bold font-mono text-[#111827] mt-1">{resumo.NetPointsTotal || 0}</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280]">Créditos</p>
          <p className="text-xl font-bold font-mono text-[#111827] mt-1">{resumo.CreditosTotal || 0}</p>
        </div>
      </div>

      {/* Por Tipo (chart + table) */}
      {porTipo.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
            <h3 className="text-sm font-semibold text-[#111827] mb-4">Distribuição por Tipo</h3>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart><Pie data={porTipo} dataKey="TotalValor" nameKey="TipoComissao" cx="50%" cy="50%" outerRadius={80} label={({TipoComissao}) => TipoComissao}>
                  {porTipo.map((t, i) => <Cell key={t.TipoComissao || `cell-${i}`} fill={COLORS[i % COLORS.length]} />)}
                </Pie><Tooltip formatter={(v) => fmt(v)} /></PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
            <h3 className="text-sm font-semibold text-[#111827] mb-4">Totais por Tipo</h3>
            <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-[#E5E7EB]">
                <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] pb-2">Tipo</th>
                <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] pb-2">Qtd</th>
                <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] pb-2">Valor</th>
              </tr></thead>
              <tbody>{porTipo.map((t, i) => (
                <tr key={t.TipoComissao || `row-${i}`} className="border-b border-[#F3F4F6]">
                  <td className="py-2 text-sm text-[#111827]">{t.TipoComissao}</td>
                  <td className="py-2 text-sm text-right text-[#6B7280]">{t.Quantidade}</td>
                  <td className="py-2 text-sm font-mono text-right text-[#0F1829]">{fmt(t.TotalValor)}</td>
                </tr>
              ))}</tbody>
            </table>
            </div>
          </div>
        </div>
      )}

      {/* Detalhes */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-[#F9FAFB] border-b border-[#E5E7EB]">
          <h3 className="text-sm font-semibold text-[#111827]">Detalhes ({detalhes.length} registos)</h3>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
            <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Data</th>
            <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Tipo</th>
            <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Enc.</th>
            <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">NP Pessoal</th>
            <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">NP Rede</th>
            <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Créditos</th>
            <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Valor</th>
          </tr></thead>
          <tbody>
            {detalhes.map((d, i) => (
              <tr key={`det-${d.ID_Encomenda || ''}-${d.Data || ''}-${d.TipoComissao || ''}-${i}`} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]">
                <td className="px-4 py-2 text-sm text-[#6B7280]">{d.Data ? new Date(d.Data).toLocaleDateString('pt-PT') : '-'}</td>
                <td className="px-4 py-2 text-sm text-[#111827]">{d.TipoComissao}</td>
                <td className="px-4 py-2 text-sm font-mono">{d.ID_Encomenda ? <span onClick={() => navigate(`/encomendas/${d.ID_Encomenda}`)} className="text-[#0F1829] underline cursor-pointer hover:text-[#001f7a]">{d.ID_Encomenda}</span> : '-'}</td>
                <td className="px-4 py-2 text-sm font-mono text-right">{d.NetPointsPessoal || 0}</td>
                <td className="px-4 py-2 text-sm font-mono text-right">{d.NetPointsRede || 0}</td>
                <td className="px-4 py-2 text-sm font-mono text-right">{d.Creditos || 0}</td>
                <td className="px-4 py-2 text-sm font-mono text-right font-semibold text-[#0F1829]">{fmt(d.ValorComissao)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {detalhes.length === 0 && <div className="text-center py-8 text-[#6B7280]">Sem detalhes</div>}
      </div>
    </div>
  );
}
